const MELP_ADMIN_PREFIX = 'melp/v1';
const MELP_ADMIN_V2_PREFIX = 'melp/v2';

const PARENT_COMPANY_ENDPOINTS = {
  getParentCompanyHierarchy: 'parentCompanies/hierarchy',
  getParentCompanyHierarchyById: 'parentCompanies/{id}/hierarchy',
  updateDefaultLanguage: 'parentCompanies/settings',
};

const START_GUIDE_ENDPOINTS = {
  actionNeeded: 'startGuide/actionNeeded',
  sixSteps: 'startGuide/sixSteps',
};

const PERMISSION_ENDPOINTS = {
  localPermissions: 'permissions/local',
  globalPermissions: 'permissions/global',
  companyGroupPermissions: 'permissions/companyGroup',
  companyPermissions: 'permissions/company',
};

const MARKETPLACE_SUPPLIERS = {
  marketplaceSuppliers: `${MELP_ADMIN_PREFIX}/marketplaceSupplier`,
  marketplaceSupplier: (id: string) =>
    `${MELP_ADMIN_PREFIX}/marketplaceSupplier/${id}`,
};

export const Endpoints = {
  ...PARENT_COMPANY_ENDPOINTS,
  ...START_GUIDE_ENDPOINTS,
  ...PERMISSION_ENDPOINTS,
  ...MARKETPLACE_SUPPLIERS,
  benefitTemplates: {
    root: 'benefitPlanTemplate',
    byId: {
      root: 'benefitPlanTemplate/:benefitTemplateId',
      copy: 'benefitPlanTemplate/:benefitTemplateId/copy',
    },
    create: 'benefitPlanTemplate/create',
  },
  auth: {
    provider: 'auth/provider',
  },
  feedback: {
    root: 'feedback',
    byId: 'feedback/:feedbackId',
    bulk: {
      root: 'feedback/bulk',
      status: 'feedback/status/bulk',
    },
  },
  clientAdmin: {
    permissions: {
      roles: {
        root: 'admin-permissions/roles',
        byId: 'admin-permissions/roles/:roleId',
        copy: 'admin-permissions/roles/:roleId/copy',
        bulkUpdate: 'admin-permissions/roles/permissions/bulk',
      },
      permissions: 'admin-permissions/permissions',
    },
    search: {
      root: 'search/:query',
    },
    clients: {
      root: 'parentCompanies',
      byId: {
        root: 'parentCompanies/:clientId',
        admins: {
          root: 'parentCompanies/:clientId/admins',
          byId: 'parentCompanies/:clientId/admins/:adminId',
          create: 'parentCompanies/:clientId/admins/create',
          sendInvite: 'parentCompanies/:clientId/admins/:adminId/resendInvite',
        },
        companies: {
          root: 'parentCompanies/:clientId/companies',
        },
      },
      create: 'parentCompanies/create',
      own: 'parentCompanies/own',
    },
    companies: {
      root: 'companies',
      orgStructure: {
        export: {
          xls: 'companies/org-structure/export/excel',
        },
      },
      byId: {
        root: 'companies/:companyId',
        settings: 'companies/:companyId/settings',
        contacts: 'companies/:companyId/contacts',
      },
      create: 'companies/create',
      contacts: {
        byId: 'companies/contacts/:contactId',
      },
      admins: {
        root: 'admins',
        byId: 'admins/:adminId',
        me: 'admins/me',
        create: {
          admin: 'admins/create',
          programmatic: 'admins/createProgrammatic',
        },
        delete: {
          hr: 'admins/hr/:adminId',
          melp: 'admins/melp/:adminId',
        },
        invite: 'admins/:adminId/resendInvite',
        tokens: {
          root: 'admins/:adminId/tokens',
          byId: 'admins/:adminId/tokens/:jti',
        },
      },
    },
    departments: {
      root: 'departments',
      byId: 'departments/:departmentId',
    },
    teams: {
      root: 'teams',
      byId: 'teams/:teamId',
    },
    employee: {
      birthdays: 'employee/birthdays',
      workAnniversaries: 'employee/workAnniversaries',
    },
    benefitPlans: {
      byId: (benefitPlanId: string) => ({
        marketplaceItems: {
          root: `benefitPlans/${benefitPlanId}/marketplaceItems`,
        },
      }),
    },
    transmissionActs: {
      root: 'transmission-act',
      balance: 'transmission-act/balance',
      deposits: 'transmission-act/deposits',
      byId: {
        root: 'transmission-act/:transmissionActId',
      },
      export: {
        xls: 'transmission-act/export/excel',
      },
    },
    alternatives: {
      root: 'benefitAlternatives',
      export: {
        xls: 'benefitAlternatives/export/excel',
      },
      byId: {
        root: 'benefitAlternatives/:alternativeId',
        employeeGroups: {
          root: 'benefitAlternatives/:alternativeId/employeeGroups',
          assignments:
            'benefitAlternatives/:alternativeId/employeeGroups/assignments',
        },
        employees: {
          root: 'benefitAlternatives/:alternativeId/employees',
          assignments:
            'benefitAlternatives/:alternativeId/employees/assignments',
          byId: {
            lockSelection:
              'benefitAlternatives/:alternativeId/employees/:employeeId/expire',
            selectBenefits:
              'benefitAlternatives/:alternativeId/employees/:employeeId/benefits',
          },
        },
        translations: 'benefitAlternatives/:alternativeId/translations',
        settings: 'benefitAlternatives/:alternativeId/settings',
        benefits: 'benefitAlternatives/:alternativeId/benefits',
        status: 'benefitAlternatives/:alternativeId/status',
        deadline: 'benefitAlternatives/:alternativeId/bulk/change-deadline',
        lockSelection: 'benefitAlternatives/:alternativeId/bulk/lock-selection',
        selectBenefits:
          'benefitAlternatives/:alternativeId/bulk/select-benefits',
        copy: 'benefitAlternatives/:alternativeId/copy',
      },
      reports: {
        byBenefits: {
          root: 'benefitAlternatives/:alternativeId/report/benefit',
          export: {
            xls: 'benefitAlternatives/:alternativeId/report/benefit/export/excel',
          },
        },
        byBenefitCategory: {
          root: 'benefitAlternatives/:alternativeId/report/benefit-category',
          export: {
            xls: 'benefitAlternatives/:alternativeId/report/benefit-category/export/excel',
          },
        },
        byCompany: {
          root: 'benefitAlternatives/:alternativeId/report/company',
          export: {
            xls: 'benefitAlternatives/:alternativeId/report/company/export/excel',
          },
        },
        byCustomCategory: {
          root: 'benefitAlternatives/:alternativeId/report/employee-category/:customCategoryId',
          export: {
            xls: 'benefitAlternatives/:alternativeId/report/employee-category/:customCategoryId/export/excel',
          },
        },
      },
    },
    shop: {
      root: 'marketplaceItems',
      selection: {
        byId: 'marketplaceItems/selection/:shopItemId',
        bulk: 'marketplaceItems/selection/bulk',
      },
      selected: 'marketplaceItems/selection',
      byBenefit: {
        root: 'marketplaceItems/benefitPlan/:benefitId',
        selected: 'benefitPlans/:benefitId/marketplaceItems',
      },
      internal: {
        root: 'marketplace-company-items',
        byId: 'marketplace-company-items/:itemId',
        inventory: {
          root: 'marketplace-company-items/variation/inventory',
          export: {
            xls: 'marketplace-company-items/variation/inventory/export/excel',
          },
          byId: {
            root: 'marketplace-company-items/variation/:variantId/inventory',
            export: {
              xls: 'marketplace-company-items/variation/:variantId/inventory/export/excel',
            },
            updateLowQuantity:
              'marketplace-company-items/variation/:variantId/low-inventory-alert',
          },
        },
      },
    },
    shopUsers: {
      root: 'shopUsers',
      byId: 'shopUsers/:shopUserId',
    },
    brands: {
      root: 'brands',
      byId: {
        root: 'brands/:brandId',
      },
      selection: {
        byId: 'brands/selection/:brandId',
        bulk: 'brands/selection/bulk',
      },
    },
    suppliers: {
      root: 'marketplaceSupplier',
    },
    wallets: {
      root: 'employee/wallet',
      totals: 'employee/wallet/total',
      notifications: 'employee/wallet/notifications',
      export: {
        xls: 'employee/wallet/export/excel',
      },
    },
    benefitWallets: {
      root: 'employee/benefit-wallet',
      totals: 'employee/benefit-wallet/total',
      export: {
        xls: 'employee/benefit-wallet/export/excel',
      },
    },
    recognitionWallets: {
      root: 'employee/recognition-wallet',
      totals: 'employee/recognition-wallet/total',
      export: {
        xls: 'employee/recognition-wallet/export/excel',
      },
    },
    expenses: {
      root: 'expenses',
      byId: 'expenses/:expenseId',
      import: {
        xls: 'expenses/import/excel',
      },
      export: {
        xls: 'expenses/export/excel',
      },
      update: {
        status: {
          bulk: 'expenses/status/bulk',
        },
      },
    },
    logs: {
      root: 'actionLog',
    },
    claims: {
      root: 'claim-reimbursement',
      byId: 'claim-reimbursement/:claimId',
      bulk: 'claim-reimbursement/bulk',
      export: {
        xls: 'claim-reimbursement/export/excel',
      },
    },
    recognitions: {
      root: 'recognition/feed',
      byId: 'recognition/:recognitionId',
      details: 'recognition/details/:recognitionId',
      settings: 'recognition/settings',
      expiration: 'recognition/manual-expiration',
      export: {
        xls: 'recognition/feed/exportToExcel',
        template: 'recognition/import/excel',
      },
      approve: {
        byId: 'recognition/approval/:recognitionId',
      },
      import: 'recognition/import/excel',
      recipients: {
        root: 'recognition/recipients',
        byId: 'recognition/recipients/employee/:employeeId',
        export: {
          xls: 'recognition/recipients/exportToExcel',
        },
      },
      givers: {
        root: 'recognition/givers',
        byId: 'recognition/givers/employee/:employeeId',
        export: {
          xls: 'recognition/givers/exportToExcel',
        },
      },
      rewards: {
        root: 'recognition/marketplaceItems',
        byId: 'recognition/marketplaceItems/:marketplaceItemId',
        config: 'recognition/marketplaceItems/assignmentFilter',
        link: 'recognition/marketplaceItems/link',
        unlink: 'recognition/marketplaceItems/unlink',
      },
      brands: {
        root: 'recognition/brands',
        byId: 'recognition/brands/:brandId',
        selection: {
          bulk: 'recognition/brands/link',
        },
        config: {
          root: 'recognition/brands/assignment-filter',
        },
      },
      achievements: {
        root: 'recognition/achievements',
        byId: 'recognition/achievements/:achievementId',
        priorities: 'recognition/achievements/priority',
      },
      allowances: {
        root: 'recognition/allowance/overview',
        export: {
          xls: 'recognition/allowance/overview/exportToExcel',
        },
        byEmployee: 'recognition/allowance/overview/employee/:employeeId',
        rules: {
          root: 'recognition/allowance-rules',
          byId: 'recognition/allowance-rules/:allowanceRuleId',
        },
      },
      leaderboard: {
        root: 'recognition/leaderboard',
        export: {
          xls: 'recognition/leaderboard/exportToExcel',
        },
      },
      comments: 'comment/recognition',
    },
    employees: {
      root: 'employee',
      byId: {
        root: 'employee/:employeeId',
        expenses: {
          root: 'employee/:employeeId/wallet/expenses',
        },
        walletBenefits: {
          root: 'employee/:employeeId/wallet/benefits',
        },
        walletRecognitions: {
          root: 'employee/:employeeId/wallet/recognition',
        },
        unlink: 'employee/:employeeId/unlink',
      },
      create: 'employee/create',
      jobTitles: 'employee/job-titles',
      bulk: {
        update: 'employee/bulkAction',
      },
      invitations: 'notification/invitation',
      export: {
        xls: 'employee/exportToExcel/new',
        blueprint: {
          root: 'employee/export/excel/blueprint',
          prepare: 'employee/export/excel/blueprint/prepare',
        },
      },
      import: {
        blueprint: 'employee/import/excel/blueprint',
      },
      consents: {
        import: {
          xls: 'employee/consents/import/excel',
        },
        export: {
          xls: 'employee/consents/export/excel',
        },
      },
      orgStructure: {
        export: {
          xls: 'employee/org-structure/export/excel',
        },
        import: {
          xls: 'employee/org-structure/import/excel',
        },
      },
      updateAssignments: 'employee/:employeeId/benefits/:benefitId',
      customCategories: {
        root: 'employee/custom-categories',
        create: 'employeeCategories/create',
        byId: {
          root: 'employeeCategories/:categoryId',
          values: {
            root: 'employeeCategories/:categoryId/values',
            byId: 'employeeCategories/:categoryId/values/:valueId',
          },
        },
      },
    },
    employeeGroups: {
      root: 'employeeGroups',
      byId: {
        root: 'employeeGroups/:employeeGroupId',
        copy: 'employeeGroups/:employeeGroupId/copy',
      },
      export: {
        xls: 'employeeGroups/export/excel',
      },
    },
    benefits: {
      root: 'benefitPlans',
      byId: {
        root: 'benefitPlans/:benefitId',
        content: 'benefitPlans/:benefitId/content',
        copy: 'benefitPlans/:benefitId/copy',
        byShopItem: 'benefitPlans/:benefitId/marketplaceItems/:shopItemId',
        shopItemsConfig:
          'benefitPlans/:benefitId/marketplaceItems/assignmentFilter',
        bulk: {
          assign: 'benefitPlans/:benefitId/marketplaceItems/link/bulk',
          unassign: 'benefitPlans/:benefitId/marketplaceItems/unlink/bulk',
          linkSelection:
            'benefitPlans/:benefitId/marketplaceItems/link/selection',
        },
      },
      employee: 'employee/:employeeId/benefits',
      export: {
        xls: 'benefitPlans/exportToExcel',
      },
      categories: {
        root: 'benefitPlans/categories',
        byId: 'benefitPlans/categories/:category',
      },
      brands: {
        root: 'benefitPlans/:benefitId/brands',
        byId: 'benefitPlans/:benefitId/brands/:brandId',
        selection: {
          bulk: 'benefitPlans/:benefitId/brands/link/bulk',
        },
        config: {
          root: 'benefitPlans/:benefitId/brands/assignment-filter',
        },
      },
    },
    assignments: {
      employees: {
        benefits: {
          root: 'employees/:employeeId/assignments/benefits',
          bulk: 'employees/:employeeId/assignments/benefits/bulk',
          byAssignmentId: 'employees/assignments/benefits/:assignmentId',
          preview: {
            root: 'employees/:employeeId/benefit-assignments',
          },
          timeline: {
            root: 'employees/:employeeId/benefit-assignments/:benefitId',
          },
        },
        employeeGroups: {
          root: 'employees/:employeeId/assignments/employee-groups',
          bulk: 'employees/:employeeId/assignments/employee-groups/bulk',
          byAssignmentId: 'employees/assignments/employee-groups/:assignmentId',
        },
      },
      employeeGroups: {
        benefits: {
          root: 'employee-groups/:employeeGroupId/assignments/benefits',
          bulk: 'employee-groups/:employeeGroupId/assignments/benefits/bulk',
        },
        employees: {
          root: 'employee-groups/:employeeGroupId/assignments/employees',
          bulk: 'employee-groups/:employeeGroupId/assignments/employees/bulk',
        },
      },
      benefits: {
        employees: {
          root: 'benefits/:benefitId/assignments/employees',
          bulk: 'benefits/:benefitId/assignments/employees/bulk',
          export: {
            xls: 'benefits/:benefitId/assignments/employees/export/excel',
          },
        },
        employeeGroups: {
          root: 'benefits/:benefitId/assignments/employee-groups',
          bulk: 'benefits/:benefitId/assignments/employee-groups/bulk',
          byAssignmentId: 'benefits/assignments/employee-groups/:assignmentId',
        },
      },
      history: {
        employee_benefit:
          'employees/:employeeId/assignments/benefits/:benefitId/history',
        benefit_employeeGroup:
          'benefits/:benefitId/assignments/employee-groups/:employeeGroupId/history',
        employee_employeeGroup:
          'employees/:employeeId/assignments/employee-groups/:employeeGroupId/history',
      },
    },
    orders: {
      root: 'marketplaceOrders',
      byId: 'marketplaceOrders/:orderId',
      bulk: {
        edit: 'marketplaceOrders/bulkStatus',
      },
      export: {
        xls: 'marketplaceOrders/export/excel',
      },
    },
    automations: {
      root: 'automations',
      jobTitles: 'automations/job-titles',
      byId: {
        root: 'automations/:automationId',
        execute: 'automations/:automationId/execute',
      },
    },
    integrations: {
      root: 'integrations',
      byId: 'integrations/:integrationId',
      models: {
        root: 'integrationTypes',
        byId: 'integrationTypes/:integrationModelId',
      },
      logItems: 'client-integration-log-items/:processId',
      logs: 'client-integration-logs/:integrationModelId',
      apiLogs: 'integrations/:integrationId/logs',
    },
    surveys: {
      root: 'surveys',
      ai: {
        trainingGeneration: 'surveys/ai/training-generation',
        trainingQuestionImport: 'surveys/ai/training-question-import',
      },
      byId: (surveyId: string) => ({
        root: `surveys/${surveyId}`,
        questions: `surveys/${surveyId}/questions`,
        groupAssignments: `surveys/${surveyId}/group-assignments`,
        activate: `surveys/${surveyId}/activate`,
        deactivate: `surveys/${surveyId}/deactivate`,
        results: `surveys/${surveyId}/results`,
        exportToExcel: `surveys/${surveyId}/exportToExcel`,
        sendNotifications: `surveys/${surveyId}/send-notifications`,
        resendNotifications: `surveys/${surveyId}/resend-notifications`,
        submissions: {
          root: `surveys/${surveyId}/submissions`,
          byId: `surveys/${surveyId}/submissions/:submissionId`,
          pending: `surveys/${surveyId}/pending-submissions`,
        },
      }),
    },
    news: {
      root: 'news',
      create: 'news/create',
      export: {
        xls: 'news/export/excel',
      },
      bulk: {
        root: 'news/bulk',
      },
      byId: {
        root: 'news/:newId',
        copy: 'news/:newId/copy',
        recipients: 'news/:newId/recipients',
        assignRecipients: 'news/:newId/assign/recipients',
        publishNow: 'news/:newId/publish-now',
      },
      categories: {
        root: 'news-categories',
        byId: 'news-categories/:categoryId',
      },
      comments: {
        root: 'comment/news',
      },
    },
    discounts: {
      root: 'discount',
      bulkUpdate: 'discount/bulkAction',
      usage: {
        root: 'discount/usage',
        export: {
          xls: 'discount/usage/export/excel',
        },
      },
    },
    trustBox: {
      root: 'trustbox',
      byId: 'trustbox/:trustboxItemId',
    },
    comments: {
      root: 'comment',
      byId: 'comment/:commentId',
      bulk: 'comment/bulk',
      update: {
        status: 'comment/status/bulk',
      },
    },
    reactions: {
      root: 'reactions',
      byHost: 'reactions/:hostId',
    },
    images: {
      generate: 'ai/generate-image',
    },
    translations: {
      translate: 'ai/translate-text',
    },
    employeeEngagement: {
      root: 'ai/predictions/employee-engagement',
      byId: 'ai/predictions/employee-engagement/:employeeId',
    },
    jobs: {
      status: 'jobs/status',
    },
  },
  melpAdmin: {
    notifications: {
      push: `${MELP_ADMIN_PREFIX}/notification/push`,
    },
    admins: {
      root: `${MELP_ADMIN_PREFIX}/admins`,
      byId: `${MELP_ADMIN_PREFIX}/admins/:adminId`,
      create: `${MELP_ADMIN_PREFIX}/admins/create`,
    },
    clients: {
      byId: `${MELP_ADMIN_PREFIX}/parentCompanies/:clientId`,
      unlinkAppUsers: `${MELP_ADMIN_PREFIX}/parentCompanies/:clientId/unlink-app-users`,
      deposits: {
        byId: `${MELP_ADMIN_PREFIX}/deposits/:depositId`,
        clients: {
          root: `${MELP_ADMIN_PREFIX}/deposits/parent-companies/:clientId`,
          history: `${MELP_ADMIN_PREFIX}/deposits/parent-companies/:clientId/deposits`,
          balances: `${MELP_ADMIN_PREFIX}/deposits/parent-companies/deposits`,
          enableDeposits: `${MELP_ADMIN_PREFIX}/deposits/parent-companies/:clientId/tracking`,
        },
        companies: {
          root: `${MELP_ADMIN_PREFIX}/deposits/companies/:companyId`,
          settings: `${MELP_ADMIN_PREFIX}/deposits/companies/:companyId/settings`,
          expenses: `${MELP_ADMIN_PREFIX}/deposits/companies/:companyId/expenses`,
          balances: `${MELP_ADMIN_PREFIX}/deposits/companies/deposits`,
          export: {
            xls: `${MELP_ADMIN_PREFIX}/deposits/companies/:companyId/export/excel`,
          },
        },
        events: {
          root: `${MELP_ADMIN_PREFIX}/deposits/tracking`,
          byId: `${MELP_ADMIN_PREFIX}/deposits/tracking/:eventId`,
        },
        pendingActions: `${MELP_ADMIN_PREFIX}/deposits/balances/requiresAction`,
      },
      balance: {
        export: {
          xls: `${MELP_ADMIN_PREFIX}/parentCompanies/:clientId/balance/:companyId/exportToExcel`,
        },
      },
    },
    transmissionActs: {
      root: 'transmission-act',
      byId: {
        root: 'transmission-act/:transmissionActId',
        export: {
          xls: 'transmission-act/:transmissionActId/export/excel',
          pdf: 'transmission-act/:transmissionActId/export/pdf',
        },
        send: 'transmission-act/:transmissionActId/send/email',
      },
      byClient: 'transmission-act/parent-company/:clientId',
    },
    brands: {
      root: `${MELP_ADMIN_PREFIX}/brands`,
      byId: {
        root: `${MELP_ADMIN_PREFIX}/brands/:brandId`,
        content: `${MELP_ADMIN_PREFIX}/brands/:brandId/content`,
        cards: `${MELP_ADMIN_PREFIX}/brands/:brandId/cards`,
      },
      export: {
        xls: `${MELP_ADMIN_PREFIX}/brands/export/excel`,
      },
      generate: {
        content: `${MELP_ADMIN_PREFIX}/ai/brand-content-suggestion`,
      },
    },
    cards: {
      root: `${MELP_ADMIN_PREFIX}/cards`,
      byId: {
        root: `${MELP_ADMIN_PREFIX}/cards/:cardId`,
        update: `${MELP_ADMIN_PREFIX}/brands/cards/:cardId`,
        content: `${MELP_ADMIN_PREFIX}/cards/:cardId/content`,
        prices: `${MELP_ADMIN_PREFIX}/cards/:cardId/prices`,
      },
      bulk: {
        xls: `${MELP_ADMIN_PREFIX}/cards/bulk-update/excel`,
      },
      export: {
        xls: `${MELP_ADMIN_PREFIX}/cards/export/excel`,
      },
      vouchers: {
        root: `${MELP_ADMIN_PREFIX}/vouchers/cards/:cardId`,
        byId: `${MELP_ADMIN_V2_PREFIX}/vouchers/:voucherId`,
        status: `${MELP_ADMIN_PREFIX}/vouchers/status`,
        import: {
          xls: `${MELP_ADMIN_V2_PREFIX}/vouchers/import/excel`,
          files: `${MELP_ADMIN_V2_PREFIX}/vouchers/import/files`,
        },
        export: {
          xls: `${MELP_ADMIN_PREFIX}/vouchers/export/excel`,
        },
      },
    },
    prompts: {
      root: `${MELP_ADMIN_PREFIX}/ai/prompt`,
      byId: `${MELP_ADMIN_PREFIX}/ai/prompt/:promptId`,
    },
    orders: {
      root: `${MELP_ADMIN_PREFIX}/marketplaceOrders`,
      byId: `${MELP_ADMIN_PREFIX}/marketplaceOrders/:orderId`,
      filterValues: `${MELP_ADMIN_PREFIX}/marketplaceOrders/filterValues`,
      bulk: {
        update: `${MELP_ADMIN_PREFIX}/marketplaceOrders/bulkAction`,
        xls: `${MELP_ADMIN_PREFIX}/marketplaceOrders/bulk-update/excel`,
      },
      export: {
        xls: `${MELP_ADMIN_PREFIX}/marketplaceOrders/exportToExcel`,
      },
    },
    discounts: {
      root: `${MELP_ADMIN_PREFIX}/discount`,
      create: `${MELP_ADMIN_PREFIX}/discount/create`,
      byId: `${MELP_ADMIN_PREFIX}/discount/:discountId`,
      export: {
        xls: `${MELP_ADMIN_PREFIX}/discount/exportToExcel`,
      },
      codes: {
        root: `${MELP_ADMIN_PREFIX}/discount/:discountId/uniqueCodes`,
        status: `${MELP_ADMIN_PREFIX}/discount/uniqueCode/status`,
        import: {
          xls: `${MELP_ADMIN_PREFIX}/discount/uniqueCode/import/excel`,
        },
      },
      feedback: {
        root: 'discount/feedback',
        export: {
          xls: 'discount/feedback/export-to-excel',
        },
      },
    },
    contentReviews: {
      root: `${MELP_ADMIN_PREFIX}/contentReviews`,
      pendingCount: `${MELP_ADMIN_PREFIX}/contentReviews/pendingCount`,
      byId: `${MELP_ADMIN_PREFIX}/contentReviews/:reviewId`,
      approve: `${MELP_ADMIN_PREFIX}/contentReviews/:reviewId/approve`,
      reject: `${MELP_ADMIN_PREFIX}/contentReviews/:reviewId/reject`,
    },
    terms: {
      root: `${MELP_ADMIN_PREFIX}/terms`,
      byId: `${MELP_ADMIN_PREFIX}/terms/:termId`,
    },
    shopItems: {
      root: `${MELP_ADMIN_PREFIX}/marketplaceItems`,
      create: `${MELP_ADMIN_PREFIX}/marketplaceSupplier/:supplierId/items`,
      byId: `${MELP_ADMIN_V2_PREFIX}/marketplaceItems/:shopItemId`,
      export: {
        xls: `${MELP_ADMIN_PREFIX}/marketplaceItems/exportToExcel`,
      },
      import: {
        updatesFile: `${MELP_ADMIN_PREFIX}/marketplaceItems/import/update/excel`,
      },
      info: `${MELP_ADMIN_PREFIX}/marketplaceItems/info`,
    },
    taxExemption: {
      byMarketplaceItem: 'tax-exemption/marketplace-item/:shopItemId',
      byId: 'tax-exemption/:taxExemptionId',
      bulk: 'tax-exemption/bulk',
    },
    shopVouchers: {
      root: `${MELP_ADMIN_PREFIX}/vouchers/marketplaceItem/:shopItemId`,
      byId: `${MELP_ADMIN_PREFIX}/vouchers/:voucherId`,
      bulk: {
        update: `${MELP_ADMIN_PREFIX}/vouchers/bulk`,
        delete: `${MELP_ADMIN_PREFIX}/vouchers/bulkDelete`,
      },
      import: {
        xls: `${MELP_ADMIN_PREFIX}/vouchers/import/excel`,
        files: `${MELP_ADMIN_PREFIX}/vouchers/import/files`,
      },
      export: {
        xls: `${MELP_ADMIN_PREFIX}/vouchers/export/excel`,
      },
    },
    suppliers: {
      root: 'marketplaceSupplier',
      byId: 'marketplaceSupplier/:supplierId',
      statistics: 'marketplaceSupplier/statistics',
      enablePartnerPortal:
        'marketplaceSupplier/:supplierId/enablePartnerPortal',
      openPartnerPortal: 'marketplaceSupplier/:supplierId/openPartnerPortal',
      disablePartnerPortal:
        'marketplaceSupplier/:supplierId/disablePartnerPortal',
      admins: 'marketplaceSupplier/:supplierId/admins',
      adminById: 'marketplaceSupplier/:supplierId/admins/:adminId',
      linkPartnerAdminCognito:
        'marketplaceSupplier/:supplierId/admins/:adminId/linkCognito',
      createPartnerAdminCognito:
        'marketplaceSupplier/:supplierId/admins/:adminId/createCognito',
      earningPeriods: 'marketplaceSupplier/:supplierId/earningPeriods',
      closeEarningPeriod: 'marketplaceSupplier/:supplierId/closeEarningPeriod',
    },
    reports: {
      export: {
        expiredBenefits: `${MELP_ADMIN_PREFIX}/employee/wallet/expiredBenefits/exportToExcel`,
        searchAttempts: `${MELP_ADMIN_PREFIX}/requestLogs/exportToExcel`,
        shopItemsContent: `${MELP_ADMIN_PREFIX}/marketplaceItems/reports/marketplaceItemsContent/exportToExcel`,
        shopItemsPriceSettings: `${MELP_ADMIN_PREFIX}/marketplaceItems/reports/marketplaceItemsPrices/exportToExcel`,
        discountsUsage: `${MELP_ADMIN_PREFIX}/discount/delta/exportToExcel`,
        vouchersStock: `${MELP_ADMIN_PREFIX}/vouchers/report/stock/excel`,
      },
    },
    marketplaceItems: {
      byId: (itemId: string) => ({
        root: `${MELP_ADMIN_PREFIX}/marketplaceItems/${itemId}`,
        translations: `${MELP_ADMIN_PREFIX}/marketplaceItems/${itemId}/translations`,
        prices: {
          root: `${MELP_ADMIN_PREFIX}/marketplaceItems/${itemId}/prices`,
          byId: (priceId: string) => ({
            root: `${MELP_ADMIN_PREFIX}/marketplaceItems/${itemId}/prices/${priceId}`,
            translations: {
              root: `${MELP_ADMIN_PREFIX}/marketplaceItems/${itemId}/prices/${priceId}/translations`,
              byId: (translationId: string) => ({
                root: `${MELP_ADMIN_PREFIX}/marketplaceItems/${itemId}/prices/${priceId}/translations/${translationId}`,
              }),
            },
          }),
        },
      }),
      translations: {
        byId: (translationId: string) => ({
          root: `${MELP_ADMIN_PREFIX}/marketplaceItems/translations/${translationId}`,
        }),
      },
    },
    logs: {
      root: `${MELP_ADMIN_PREFIX}/actionLog`,
    },
    parentCompanies: {
      byId: (parentCompanyId: string) => ({
        root: `${MELP_ADMIN_PREFIX}/parentCompanies/${parentCompanyId}`,
      }),
    },
    v2: {
      marketplaceItems: {
        byId: (itemId: string) => ({
          root: `${MELP_ADMIN_V2_PREFIX}/marketplaceItems/${itemId}`,
        }),
      },
    },
  },
  files: {
    byId: 'v1/files/:fileId',
    upload: 'v1/files/upload',
  },
  public: {
    surveys: {
      byId: 'surveys/:surveyId/employee/:employeeId',
    },
  },
};
